import React, { useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import addLabel from 'helpers/addLabel';
import addRecent from 'helpers/addRecent';
import sortByRecent from 'components/TopicFilters/utils/sortByRecent';
import getInitialSalesType from 'helpers/getInitialSalesType';
import getSchema from 'schemas/componentSettings/browseOpus';
import useComponentSettings from 'hooks/useComponentSettings';
import { checkIsExternalUser } from 'redux/reducers/authentication';
import { loadGenres } from 'redux/reducers/genres';
import { RootState } from 'redux/rootReducer';

import { ReactComponent as IconClearFilter } from 'icons/icon-clear-filter.svg';
import { GenreDropdown } from './GenreDropdown';

import { ITopicFilterState } from 'components/TopicFilters/shared';

import { Button as TopicFilterButton } from 'components/TopicFilters/TopicFilters.styles';

const disabledMessage = 'Only available for Tracks and Releases';

interface IGenreProps {
  clearFilterUrl: Function;
  filters: ITopicFilterState;
  setFilter: Function;
  defaultLabel: string;
  recentFilters: any;
}

const GenreFilterContainer: React.FC<IGenreProps> = (props) => {
  const { clearFilterUrl, filters, setFilter, recentFilters } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadGenres());
  }, [dispatch]);

  const getRecentFiltersFor = useCallback(() => {
    const result = recentFilters.filter(
      (recent: any) => recent.filterType === 'genre'
    );
    result.splice(3);
    return result;
  }, [recentFilters]);

  const genres = useSelector((state: RootState) => state.genres.data)
    .map(
      ({ genreCode, genreName }: { genreCode: number; genreName: string }) => ({
        id: genreCode,
        displayName: genreName,
      })
    )
    .map(addLabel as any)
    .map(addRecent(getRecentFiltersFor()) as any)
    .sort(sortByRecent as any);

  const selectedGenresFilter = useMemo(
    () =>
      genres.filter(
        (item: any): boolean =>
          !!filters?.genre && !!filters?.genre?.includes(item.id.toString())
      ),
    [filters, genres]
  );

  const isExternalUser = useSelector(checkIsExternalUser);
  const initialSalesType = getInitialSalesType(isExternalUser);
  const [{ browseEntityType }] = useComponentSettings(
    'browse-opus',
    getSchema(initialSalesType)
  );
  const disabled = useMemo(
    () => browseEntityType !== 'track' && browseEntityType !== 'release',
    [browseEntityType]
  );

  const onChange = (item: any): void => {
    const genres =
      typeof filters?.genre === 'string' ? [filters?.genre] : filters?.genre;
    const currrentGenres = genres?.map((genre: any) => parseInt(genre));
    const isChecked = currrentGenres?.includes(item?.id);

    let newGenre = [item?.id];
    if (isChecked && currrentGenres?.length) {
      // remove from current genres
      newGenre = currrentGenres?.filter((genre: any) => genre !== item?.id);
    } else if (currrentGenres?.length) {
      // add to current genres
      newGenre = [...currrentGenres, item?.id];
    }

    if (newGenre.length) {
      setFilter('genre', newGenre);
    } else {
      clearFilterUrl('genre');
    }
  };

  return (
    <GenreDropdown
      {...props}
      onChange={onChange}
      items={genres}
      selectedItems={selectedGenresFilter}
      itemToString={(item: any): string => item?.displayName}
      disabled={disabled}
      disabledMessage={disabledMessage}
      clearFilter={(): void => clearFilterUrl('genre')}
    >
      <TopicFilterButton
        borderColor={'LightGrey'}
        borderWidth={'1px'}
        borderStyle={'solid'}
        py={4}
        isActive={!!selectedGenresFilter.length}
        onClick={(): void => clearFilterUrl('genre')}
        isClearButton
      >
        <IconClearFilter />
        Clear this filter
      </TopicFilterButton>
    </GenreDropdown>
  );
};

export default GenreFilterContainer;
